export interface V2ErrorDetail {
  field?: string;
  issue?: string;
  hint?: string;
  [k: string]: unknown;
}

export class V2ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: V2ErrorDetail[],
    public readonly requestId?: string,
    /**
     * Backlog 283 — raw response body excerpt populated on the UNKNOWN
     * path so the telemetry pipeline can record what the server returned
     * even when the classifier could not match the body to a known
     * envelope shape. Truncated at {@link RAW_TEXT_LIMIT} characters.
     * Undefined when the body parsed as a known envelope.
     */
    public readonly text?: string,
  ) {
    super(message);
    this.name = 'V2ApiError';
  }
}

/**
 * Cap on the raw-body excerpt we attach to an UNKNOWN-coded V2ApiError
 * (backlog 283). The CLI's telemetry payload as a whole is small —
 * a 4 KiB tail is large enough to capture a typical 5xx HTML stack or
 * a JSON error body, small enough not to pressure the 250ms cap.
 */
export const RAW_TEXT_LIMIT = 4096;

export interface ParseErrorOptions {
  /**
   * Value of the `X-Request-Id` response header. parseErrorBody prefers
   * the value carried inside the v2 envelope when present, but falls
   * back to this when the body does not parse to a known shape — the
   * common case for backlog 283's UNKNOWN path, where the server may
   * have failed before its envelope-wrapper middleware ran.
   */
  requestId?: string;
}

export function parseErrorBody(
  status: number,
  statusText: string,
  body: unknown,
  opts: ParseErrorOptions = {},
): V2ApiError {
  if (body && typeof body === 'object') {
    const b = body as any;
    if (b.error && typeof b.error === 'object' && typeof b.error.code === 'string') {
      const envelopeRequestId =
        typeof b.error.request_id === 'string' ? b.error.request_id : undefined;
      const err = new V2ApiError(
        status,
        b.error.code,
        typeof b.error.message === 'string' ? b.error.message : statusText,
        Array.isArray(b.error.details) ? b.error.details : undefined,
        envelopeRequestId ?? opts.requestId,
      );
      // Backlog 200 — the min-cli-version middleware returns extra
      // sibling fields (`min_version`, `install_command`) alongside the
      // standard `error` envelope. Attach them to the V2ApiError so the
      // error handler can render them without re-parsing the response.
      if (status === 426 && b.error.code === 'CLI_UPGRADE_REQUIRED') {
        (err as any).minVersion = typeof b.min_version === 'string' ? b.min_version : undefined;
        (err as any).installCommand =
          typeof b.install_command === 'string' ? b.install_command : undefined;
      }
      return err;
    }
    if (typeof b.code === 'string') {
      return new V2ApiError(
        status,
        b.code,
        typeof b.error === 'string' ? b.error : statusText,
        undefined,
        opts.requestId,
      );
    }
  }
  // Backlog 283 — UNKNOWN path. The classifier could not match the body
  // to a known envelope shape, so previously every diagnostic field was
  // dropped (25/25 production UNKNOWN events had text=null, request_id=
  // null, error_status=null). Capture the raw body excerpt + the
  // X-Request-Id header so the telemetry record carries something we
  // can root-cause from.
  let rawText: string | undefined;
  if (typeof body === 'string' && body.length > 0) {
    rawText = body.slice(0, RAW_TEXT_LIMIT);
  } else if (body !== undefined && body !== null) {
    try {
      rawText = JSON.stringify(body).slice(0, RAW_TEXT_LIMIT);
    } catch {
      // Non-serializable body — leave rawText undefined.
    }
  }
  return new V2ApiError(status, 'UNKNOWN', statusText, undefined, opts.requestId, rawText);
}

const AUTH_CODES = new Set([
  'AUTH_REQUIRED', 'AUTH_EXPIRED', 'AUTH_INVALID', 'UNAUTHORIZED',
  'FORBIDDEN_ROLE', 'NO_ACTIVE_ORG', 'ORG_NOT_ACCESSIBLE',
  'INVITATION_EMAIL_MISMATCH', 'SPACE_ACCESS_DENIED',
]);

const VALIDATION_CODES = new Set([
  'VALIDATION_FAILED', 'SCOPE_INVALID', 'SPACE_REQUIRED', 'SPACE_NOT_MEMBER',
  'INSPIRED_BY_INVALID', 'RESERVED_SLUG', 'RESERVED_KEY', 'SLUG_TAKEN',
  'ALREADY_ARCHIVED', 'DEFAULT_SPACE_PROTECTED', 'LAST_ADMIN', 'INVALID_PRIMARY_ORG',
  'ALREADY_ACCEPTED', 'INVITATION_EXPIRED', 'INVITATION_REVOKED',
  'INVITATION_TOKEN_INVALID', 'NOT_A_RUNTIME', 'SPACE_NOT_IN_ORG',
  'USER_NOT_IN_ORG', 'NO_UPDATES', 'INVALID_EMAIL',
]);

const SERVER_CODES = new Set(['INTERNAL_ERROR']);

export function exitCodeFor(err: V2ApiError): number {
  // Backlog 200 — forced-update sits at exit 5 (its own dedicated band of
  // "the request was structurally rejected, not an auth/network issue").
  if (err.code === 'CLI_UPGRADE_REQUIRED') return 5;
  if (AUTH_CODES.has(err.code)) return 3;
  if (err.code.endsWith('_NOT_FOUND')) return 4;
  if (VALIDATION_CODES.has(err.code)) return 5;
  if (SERVER_CODES.has(err.code)) return 10;
  // For UNKNOWN with 5xx (except 500 which is generic), treat as server error
  if (err.code === 'UNKNOWN' && err.status >= 500 && err.status !== 500) return 10;
  return 1;
}
